import { useEffect, useState } from "react";
import { toast } from "sonner";

const ACTION_BADGE = {
  "user.create": "tag-moss",
  "user.patch": "tag-moss",
  "user.set_org": "tag-moss",
  "user.bulk_suspend": "tag-clay",
  "user.bulk_unsuspend": "tag-moss",
  "user.bulk_delete": "tag-clay",
  "listing.patch": "tag-moss",
  "listing.delete": "tag-clay",
  "listing.bulk_hide": "tag-moss",
  "listing.bulk_unhide": "tag-moss",
  "listing.bulk_delete": "tag-clay",
  "booking.force_status": "tag-moss",
  "booking.expire_pending": "tag-moss",
  "report.resolve": "tag-moss",
  "rating.delete": "tag-clay",
  "org.create": "tag-moss",
  "org.update": "tag-moss",
  "org.delete": "tag-clay",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminAuditLog({ api, t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorEmail, setActorEmail] = useState("");
  const [action, setAction] = useState("");
  const [openDetails, setOpenDetails] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (actorEmail.trim()) params.actor_email = actorEmail.trim();
      if (action.trim()) params.action = action.trim();
      const r = await api.get("/admin/audit-log", { params });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dash.failed"));
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = { limit: 5000 };
      if (actorEmail.trim()) params.actor_email = actorEmail.trim();
      if (action.trim()) params.action = action.trim();
      const r = await api.get("/admin/audit-log.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `audit_log_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("admin.audit.exported"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dash.failed"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div data-testid="admin-audit-log">
      <div className="flex flex-wrap gap-3 mb-5">
        <input
          className="input-tech max-w-[240px]"
          placeholder={t("admin.audit.actorPh")}
          value={actorEmail}
          onChange={(e) => setActorEmail(e.target.value)}
          data-testid="audit-filter-actor"
        />
        <input
          className="input-tech max-w-[240px]"
          placeholder={t("admin.audit.actionPh")}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          data-testid="audit-filter-action"
        />
        <button onClick={load} className="btn-ghost" data-testid="audit-refresh-btn">
          {t("admin.audit.refresh")}
        </button>
        <button onClick={exportCsv} className="btn-ghost" data-testid="audit-export-csv-btn">
          {t("admin.audit.exportCsv")}
        </button>
      </div>

      {loading && <div className="label-tech">…</div>}
      {!loading && items.length === 0 && (
        <div className="label-tech" data-testid="audit-empty">{t("admin.audit.empty")}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="divide-y divide-[var(--ink-200)]">
          {items.map((e) => {
            const tag = ACTION_BADGE[e.action] || "tag-ash";
            const isOpen = !!openDetails[e.id];
            const hasDetails = e.details && Object.keys(e.details).length > 0;
            return (
              <div key={e.id} className="py-3" data-testid={`audit-entry-${e.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="label-tech min-w-[140px]">{fmtDate(e.created_at)}</div>
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`tag ${tag}`}>{e.action}</span>
                      <span className="font-display text-sm">{e.summary || e.action}</span>
                    </div>
                    <div className="label-tech mt-1">
                      {t("admin.audit.by")} <span className="text-[var(--ink)]">{e.actor_email || e.actor_user_id || "—"}</span>
                      {e.target_type && (
                        <>
                          {" · "}
                          {e.target_type}
                          {e.target_id ? ` ${e.target_id}` : ""}
                        </>
                      )}
                    </div>
                    {hasDetails && (
                      <button
                        type="button"
                        onClick={() => setOpenDetails((m) => ({ ...m, [e.id]: !m[e.id] }))}
                        className="label-tech mt-1 underline"
                        data-testid={`audit-toggle-${e.id}`}
                      >
                        {isOpen ? t("admin.audit.hideDetails") : t("admin.audit.showDetails")}
                      </button>
                    )}
                    {hasDetails && isOpen && (
                      <pre className="mt-2 text-xs bg-[var(--surface-alt)] border border-[var(--ink-200)] p-2 overflow-x-auto">
                        {JSON.stringify(e.details, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
